// Copyright © 2026 Gian Marco Cappelli — SAPIENS (finsapiens.it). Tutti i diritti riservati. Sviluppato su commissione esclusiva. Vietata la riproduzione senza autorizzazione scritta.
import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Menu, X, User, LogOut, LayoutDashboard, ChevronDown, FileText, BookOpen } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const navLinks = [
    { href: '/percorsi', label: 'Percorsi' },
    { href: '/calcolatori', label: 'Calcolatori' },
    { href: '/risorse', label: 'Risorse' },
    { href: '/strumenti/simulatore-portafoglio', label: 'Portafoglio' },
    { href: '/blog', label: 'Blog' },
    { href: '/glossario', label: 'Glossario' },
    { href: '/premium', label: 'Premium', badge: false },
  ];

  // Deployment 14 — P2: Guide dropdown links
  // Deployment 28 — J1/D: aggiunte 5 guide dinamiche DB-driven (route /guide/:slug)
  const GUIDE_LINKS = [
    // Guide statiche legacy
    { href: '/guida-busta-paga',                     label: 'Guida Busta Paga',           icon: '📄' },
    { href: '/guida-dichiarazione-redditi',          label: 'Guida Dichiarazione Redditi', icon: '📋' },
    { href: '/guida-kid',                            label: 'Come leggere un KID',         icon: '📑' },
    // Guide dinamiche (Deployment 25)
    { href: '/guide/surroga-mutuo',                  label: 'Surroga Mutuo',              icon: '🏦' },
    { href: '/guide/successione-eredita',            label: 'Successione ed Eredità',      icon: '⚖️' },
    { href: '/guide/affitto-vs-acquisto',            label: 'Affitto vs Acquisto Casa',    icon: '🏠' },
    { href: '/guide/pensione-complementare',         label: 'Pensione Complementare',      icon: '👴' },
    { href: '/guide/dichiarazione-redditi-autonomi', label: 'Dichiarazione Redditi Autonomi', icon: '💼' },
  ];
  const [mobileGuidesOpen, setMobileGuidesOpen] = React.useState(false);

  const isActive = (href) => location.pathname === href;

  return (
    <nav className="sticky top-1 z-50 bg-[#1C1C1A] border-b border-white/[0.08]">
      <div className="container-wide">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-2"
            data-testid="navbar-logo"
          >
            <span className="w-2 h-2 rounded-full bg-[#C9A84C]"></span>
            <span className="text-[#E8E8E4] text-xl font-semibold tracking-widest">SAPIENS</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <React.Fragment key={link.href}>
                {/* Inject Guide dropdown after Percorsi, before Calcolatori (P2) */}
                {link.href === '/calcolatori' && (
                  <DropdownMenu key="guide-dropdown">
                    <DropdownMenuTrigger asChild>
                      <button
                        className="transition-colors font-medium flex items-center gap-1 text-[#888780] hover:text-[#E8E8E4] focus:outline-none"
                        data-testid="nav-guide"
                      >
                        Guide <ChevronDown className="w-3.5 h-3.5" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-56 bg-[#252523] border-white/[0.08]">
                      {GUIDE_LINKS.map(g => (
                        <DropdownMenuItem
                          key={g.href}
                          onClick={() => navigate(g.href)}
                          className="cursor-pointer flex items-center gap-2 text-[#E8E8E4] focus:bg-white/[0.04] focus:text-[#E8E8E4]"
                          data-testid={`nav-guide-item-${g.href.replace(/\//g, '')}`}
                        >
                          <span>{g.icon}</span> <span>{g.label}</span>
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
                <Link
                  to={link.href}
                  className={`transition-colors font-medium flex items-center gap-1.5 ${
                    isActive(link.href)
                      ? 'text-[#C9A84C] border-b-2 border-[#C9A84C] pb-0.5'
                      : 'text-[#888780] hover:text-[#E8E8E4]'
                  }`}
                  data-testid={`nav-${link.label.toLowerCase()}`}
                >
                  {link.label}
                  {link.badge && <span className="px-1 py-0.5 rounded text-[9px] font-bold" style={{ background: 'rgba(201,168,76,0.12)', color: '#C9A84C', border: '1px solid rgba(201,168,76,0.25)' }}>Presto</span>}
                </Link>
              </React.Fragment>
            ))}
          </div>

          {/* Right side actions */}
          <div className="flex items-center gap-3">
            {/* Lesson progress for logged-in users */}
            {user && (() => {
              const done = (user.completed_lessons || []).filter(id => !id.startsWith('quiz_')).length;
              const pct = Math.min(100, Math.round((done / 16) * 100));
              return (
                <div className="hidden sm:flex items-center gap-2" data-testid="lesson-progress-bar">
                  <span className="text-xs text-[#888780] whitespace-nowrap">📚 {done}/16</span>
                  <div className="w-16 h-1.5 rounded-full bg-white/[0.08]">
                    <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: '#3D7A52' }} />
                  </div>
                </div>
              );
            })()}
            {/* Auth buttons */}
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    className="flex items-center gap-2 text-[#E8E8E4] hover:bg-white/[0.08]"
                    data-testid="user-menu-trigger"
                  >
                    {user.picture ? (
                      <img 
                        src={user.picture} 
                        alt={user.name} 
                        className="w-8 h-8 rounded-full"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-[#C9A84C] flex items-center justify-center text-[#3A2800] text-sm font-bold">
                        {user.name?.split(' ').map(n => n[0]).join('').slice(0,2).toUpperCase() || 'U'}
                      </div>
                    )}
                    <span className="hidden sm:inline">{user.name?.split(' ')[0]}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 bg-[#252523] border-white/[0.08]">
                  <DropdownMenuItem 
                    onClick={() => navigate('/dashboard')} 
                    className="text-[#E8E8E4] hover:bg-white/[0.08] cursor-pointer"
                    data-testid="menu-dashboard"
                  >
                    <LayoutDashboard className="mr-2 h-4 w-4" />
                    Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => navigate('/profilo')} 
                    className="text-[#E8E8E4] hover:bg-white/[0.08] cursor-pointer"
                    data-testid="menu-profile"
                  >
                    <User className="mr-2 h-4 w-4" />
                    Profilo
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-white/[0.08]" />
                  <DropdownMenuItem 
                    onClick={handleLogout} 
                    className="text-[#E8E8E4] hover:bg-white/[0.08] cursor-pointer"
                    data-testid="menu-logout"
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    Esci
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="hidden sm:flex items-center gap-2">
                <Button 
                  variant="ghost" 
                  onClick={() => navigate('/login')}
                  className="text-[#888780] hover:text-[#E8E8E4] hover:bg-transparent"
                  data-testid="login-btn"
                >
                  Accedi
                </Button>
                <Button 
                  onClick={() => navigate('/login')}
                  className="sapiens-btn-primary"
                  data-testid="signup-btn"
                >
                  Inizia gratis
                </Button>
              </div>
            )}

            {/* Mobile menu button */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden text-[#E8E8E4] hover:bg-white/[0.08]"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-white/[0.08]">
            <div className="flex flex-col gap-2">
              {navLinks.map((link) => (
                <React.Fragment key={link.href}>
                  {/* Inject expandable Guide section before Calcolatori in mobile menu (P2) */}
                  {link.href === '/calcolatori' && (
                    <div data-testid="mobile-guide-section">
                      <button
                        onClick={() => setMobileGuidesOpen(o => !o)}
                        className="w-full flex items-center justify-between px-4 py-2 rounded-lg text-[#888780] hover:text-[#E8E8E4] hover:bg-white/[0.04]"
                        data-testid="mobile-guide-toggle"
                      >
                        <span>Guide</span>
                        <ChevronDown className={`w-4 h-4 transition-transform ${mobileGuidesOpen ? 'rotate-180' : ''}`} />
                      </button>
                      {mobileGuidesOpen && (
                        <div className="ml-4 mt-1 space-y-1">
                          {GUIDE_LINKS.map(g => (
                            <Link key={g.href} to={g.href}
                              onClick={() => { setMobileMenuOpen(false); setMobileGuidesOpen(false); }}
                              className="flex items-center gap-2 px-4 py-2 rounded-lg text-[#888780] hover:text-[#E8E8E4] hover:bg-white/[0.04]"
                              data-testid={`mobile-guide-item-${g.href.replace(/\//g, '')}`}>
                              <span>{g.icon}</span> <span>{g.label}</span>
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  <Link
                    to={link.href}
                    className={`px-4 py-2 rounded-lg transition-colors ${
                      isActive(link.href)
                        ? 'text-[#C9A84C] bg-white/[0.04]'
                        : 'text-[#888780] hover:text-[#E8E8E4] hover:bg-white/[0.04]'
                    }`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {link.label}
                  </Link>
                </React.Fragment>
              ))}
              {!user && (
                <>
                  <hr className="my-2 border-white/[0.08]" />
                  <Button 
                    variant="ghost" 
                    onClick={() => { navigate('/login'); setMobileMenuOpen(false); }}
                    className="justify-start text-[#888780] hover:text-[#E8E8E4]"
                  >
                    Accedi
                  </Button>
                  <Button 
                    onClick={() => { navigate('/login'); setMobileMenuOpen(false); }}
                    className="sapiens-btn-primary"
                  >
                    Inizia gratis
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
